/**
 * Price-precedent fixtures. The Home Group calling-card history is lifted
 * from the validation run (docs/features/price-precedent/
 * 00_spec_key_validation.md) — a real ladder by quantity, real +8–10%
 * repricing steps — so the panel is reviewed against the shape the data
 * actually has, not a tidy invention.
 */
import type {
  CataloguePriceReview,
  CataloguePriceReviewRow,
  LevelVerdict,
  PrecedentCandidate,
  PrecedentLine,
  PrecedentRow,
  PrecedentRowView,
  PriceLevel,
  PriceLevels,
  PricePrecedent,
  PricePrecedentView,
  RepeatPriceCalibration,
  RepeatPriceThresholds,
} from "../data/price-precedent-types";
import { repeatPriceVerdict } from "../domain/price-precedent";

// ── The lookup rule, mocked ────────────────────────────────────────────────
//
// The server owns these (price_precedent_module's `_assemble.py`); the mock
// adapter stands in for it, so the mirror lives here and nowhere in the
// client's own domain code.

const QUANTITY_TOLERANCE = 0.1;

function isNearQuantity(a: number, b: number): boolean {
  return b > 0 && Math.abs(a - b) <= QUANTITY_TOLERANCE * b;
}

function newestNear<R extends PrecedentRow>(
  rows: R[],
  quantity: number,
  outcome: PrecedentRow["outcome"],
): R | null {
  return (
    rows
      .filter(
        (r) => r.outcome === outcome && isNearQuantity(r.quantity, quantity),
      )
      .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null
  );
}

/** A precedent's one-line form against a quote's unit price. */
export function mockPrecedentLine(
  precedent: PricePrecedentView,
  quotedUnitPence: number | null,
): PrecedentLine {
  const nearest = precedent.nearest ?? null;
  return {
    coverage: precedent.coverage,
    nearest,
    delta_pct:
      quotedUnitPence == null || !nearest || nearest.unit_price_pence <= 0
        ? null
        : Math.round(
            ((quotedUnitPence - nearest.unit_price_pence) /
              nearest.unit_price_pence) *
              1000,
          ) / 10,
  };
}

const REFRESHED_AT = "2026-09-15T11:00:00Z";

const sold = (
  date: string,
  quantity: number,
  pricePence: number,
  cm: number,
  reference: string,
  match: PrecedentRow["match"] = "structural",
  source: PrecedentRow["source"] = "tharstern",
): PrecedentRowView => ({
  type: "price_precedent_row",
  _self: `/views/jobs/${reference}`,
  date,
  quantity,
  price_pence: pricePence,
  unit_price_pence: pricePence / quantity,
  // Percent, as the backend sends a row's margin; `cm` is written as a fraction.
  contribution_margin_pct: Math.round(cm * 1000) / 10,
  outcome: "ordered",
  source,
  match,
  reference,
  url: `/jobs/${reference}`,
});

const quoted = (
  date: string,
  quantity: number,
  pricePence: number,
  reference: string,
  estimateId: string,
): PrecedentRowView => ({
  type: "price_precedent_row",
  _self: `/views/estimates/${estimateId}`,
  date,
  quantity,
  price_pence: pricePence,
  unit_price_pence: pricePence / quantity,
  contribution_margin_pct: null,
  outcome: "quoted",
  source: "tharstern",
  match: "customer_ref",
  reference,
  url: `/estimates/${estimateId}`,
});

interface PrecedentFixture {
  coverage: PricePrecedentView["coverage"];
  customer_code: string;
  customer_name: string | null;
  spec_label: string | null;
  spec_identity?: string | null;
  keyed_on?: PricePrecedentView["keyed_on"];
  quantity: number;
  rows: PrecedentRowView[];
}

const NOTE_PRODUCT_CODE_ONLY =
  "Matched on the customer's product code (Ref4) only. The specification itself can't be keyed " +
  "for this quote, so an order for the same spec under a different product code won't show here.";
const NOTE_NOTHING_TO_KEY =
  "No product code (Ref4) is filed on this quote and its specification can't be keyed, " +
  "so there is nothing to match previous orders against.";

// Precedent links recorded in this mock session (POST /price-precedent/
// actions/link), keyed customer + spec identity. A link makes the linked job
// a `manual` row from then on — the server's tier-0 rule.
const MOCK_LINKS = new Map<string, PrecedentCandidate>();

export function recordMockPrecedentLink(
  customerCode: string,
  specIdentity: string,
  candidate: PrecedentCandidate,
): void {
  MOCK_LINKS.set(`${customerCode}|${specIdentity}`, candidate);
}

function linkedRow(candidate: PrecedentCandidate): PrecedentRowView {
  return {
    type: "price_precedent_row",
    _self:
      candidate.kind === "job"
        ? `/views/jobs/${candidate.reference}`
        : `/views/estimates/${candidate.reference}`,
    date: candidate.date,
    quantity: candidate.quantity,
    price_pence: candidate.price_pence,
    unit_price_pence: candidate.price_pence / Math.max(candidate.quantity, 1),
    contribution_margin_pct: null,
    outcome: candidate.kind === "job" ? "ordered" : "quoted",
    source: "tharstern",
    match: "manual",
    reference: candidate.reference,
    url: candidate.url,
  };
}

function precedentFor(
  fixture: PrecedentFixture,
  selfUrl: string | null = null,
): PricePrecedentView {
  const identity =
    fixture.spec_identity ??
    (fixture.spec_label && fixture.coverage !== "unavailable"
      ? `ref:${fixture.spec_label}`
      : null);
  const link = identity
    ? MOCK_LINKS.get(`${fixture.customer_code}|${identity}`)
    : undefined;
  const base: PrecedentFixture = link
    ? {
        ...fixture,
        coverage: "recorded",
        rows: [
          linkedRow(link),
          ...fixture.rows.filter((r) => r.reference !== link.reference),
        ],
      }
    : fixture;
  const keyedOn =
    base.keyed_on ??
    (base.coverage === "unavailable" ? [] : ["customer_ref" as const]);
  return {
    type: "price_precedent",
    _self: selfUrl,
    coverage: base.coverage,
    customer_code: base.customer_code,
    customer_name: base.customer_name,
    spec_label: base.spec_label,
    // Mock identity: the label stands in for the key it was built from.
    spec_identity: identity,
    keyed_on: keyedOn,
    quantity: base.quantity,
    window_months: 6,
    quantity_tolerance_pct: 10,
    refreshed_at: REFRESHED_AT,
    rows: base.rows,
    nearest:
      newestNear(
        base.rows.filter((r) => r.match === "manual"),
        base.quantity,
        "ordered",
      ) ?? newestNear(base.rows, base.quantity, "ordered"),
    last_quoted: newestNear(base.rows, base.quantity, "quoted"),
    unrecognised:
      base.coverage !== "unavailable" &&
      !base.rows.some((r) => r.outcome === "ordered"),
    note:
      base.coverage === "unavailable"
        ? NOTE_NOTHING_TO_KEY
        : keyedOn.length === 1 && keyedOn[0] === "customer_ref"
          ? NOTE_PRODUCT_CODE_ONLY
          : null,
  };
}

// ── Home Group — A6 calling card, the validation's own cluster ──────────────

const HOME_GROUP_CALLING_CARD_ROWS: PrecedentRowView[] = [
  sold("2026-09-07", 500, 9_900, 0.34, "J-118611"),
  sold("2026-08-11", 500, 9_350, 0.33, "J-118203"),
  sold("2026-07-28", 500, 9_350, 0.33, "J-117640"),
  sold("2026-07-14", 100, 6_160, 0.28, "J-117402"),
  sold("2026-07-02", 250, 6_700, 0.3, "J-117111"),
  sold("2026-06-19", 500, 8_500, 0.29, "J-116552"),
  sold("2026-06-12", 50, 5_300, 0.21, "J-116302"),
  sold("2026-06-06", 500, 8_500, 0.29, "J-116211"),
  sold("2026-05-13", 500, 7_700, 0.26, "J-115704"),
];

/** Precedent keyed by the estimate ids the exceptions cards and estimate
 *  detail fixtures use. Absent id → `unavailable` (no spec key resolvable). */
const BY_ESTIMATE: Record<string, PrecedentFixture> = {
  // est_001 — the catalogue-send fixture: a repeat annual report, sold twice
  // before at the same run length, once through the catalogue.
  est_001: {
    coverage: "recorded",
    // Catalogue-origin: the exact catalogue key as well as the product code.
    keyed_on: ["catalogue_exact", "customer_ref"],
    customer_code: "BSCH001",
    customer_name: "Business School UK",
    spec_label: "Perfect bound A4 · 4/4 · 64pp · AR-2026",
    quantity: 500,
    rows: [
      sold(
        "2026-08-02",
        500,
        1_695_000,
        0.31,
        "J-117985",
        "catalogue_exact",
        "catalogue",
      ),
      sold("2025-08-19", 500, 1_612_000, 0.29, "J-109331", "customer_ref"),
      sold("2024-08-22", 450, 1_540_000, 0.27, "J-101204", "customer_ref"),
      quoted("2026-03-11", 1_000, 2_890_000, "HDR-2026-0012", "est_090"),
    ],
  },
  // est_004 — the permanent-legacy sibling: same customer, same job, so the
  // same precedent.
  est_004: {
    coverage: "recorded",
    // Catalogue-origin: the exact catalogue key as well as the product code.
    keyed_on: ["catalogue_exact", "customer_ref"],
    customer_code: "BSCH001",
    customer_name: "Business School UK",
    spec_label: "Perfect bound A4 · 4/4 · 64pp · AR-2026",
    quantity: 500,
    rows: [
      sold("2025-08-19", 500, 1_612_000, 0.29, "J-109331", "customer_ref"),
      sold("2024-08-22", 450, 1_540_000, 0.27, "J-101204", "customer_ref"),
    ],
  },
  // est_002 — CIS estimating, a genuine first order: the lookup ran and
  // found nothing. Must render as "no previous orders", never as an empty
  // table or a missing block.
  est_002: {
    coverage: "none",
    customer_code: "cust_012",
    customer_name: "Bramley & Co",
    spec_label: "A5 · 4/4 · 8pp",
    quantity: 2_000,
    rows: [],
  },
  // est_741048 — Tharstern native, already a job; the customer reorders this
  // exact spec on a steady cadence.
  est_741048: {
    coverage: "recorded",
    customer_code: "BSCH001",
    customer_name: "Business School UK",
    spec_label: "A4 · 4/4 · 24pp · PROSP-2026",
    quantity: 1_500,
    rows: [
      sold("2025-08-21", 1_500, 405_000, 0.32, "J-109402", "customer_ref"),
      sold("2025-02-13", 2_000, 498_000, 0.33, "J-105117", "customer_ref"),
      sold("2024-09-05", 1_000, 312_000, 0.3, "J-101880", "customer_ref"),
      quoted("2026-03-02", 3_000, 690_000, "HDR-2026-0210", "est_091"),
    ],
  },
  // The exceptions board's cards (component grain — the estimate id is the
  // card's current revision).
  "est-652402-r3": {
    coverage: "recorded",
    customer_code: "HHG01",
    customer_name: "HH Associates Ltd",
    spec_label: "Acrylic freestanding units · factored",
    quantity: 120,
    rows: [
      sold("2026-02-18", 120, 9_140_000, 0.22, "J-113877", "customer_ref"),
      sold("2025-09-30", 100, 7_820_000, 0.24, "J-110412", "customer_ref"),
    ],
  },
  "est-652874-r1": {
    coverage: "recorded",
    customer_code: "PER01",
    customer_name: "Perivan - SJP Bespoke Stationery",
    spec_label: "99×210 · 4/0 · 1pp · STAT-COMPSLP",
    quantity: 250,
    rows: [
      sold("2026-05-20", 250, 5_200, 0.29, "J-116102", "customer_ref"),
      sold("2026-01-22", 250, 5_200, 0.29, "J-113790", "customer_ref"),
      sold("2025-09-10", 500, 7_100, 0.31, "J-110210", "customer_ref"),
    ],
  },
  "est-652880-r1": {
    coverage: "none",
    customer_code: "MER01",
    customer_name: "Meridian Care Group",
    spec_label: "A5 · 4/4 · 8pp",
    quantity: 2_000,
    rows: [],
  },
};

/** The composed precedent for an estimate, as `/views/estimates/{id}` and
 *  `/views/price-precedent` serve it. An estimate no fixture keys is
 *  `unavailable` — nothing to match on — never an empty `recorded`. */
export function mockPrecedentForEstimate(
  estimateId: string,
): PricePrecedentView {
  const selfUrl = `/views/price-precedent?estimate_id=${estimateId}`;
  const fixture = BY_ESTIMATE[estimateId];
  if (fixture) return precedentFor(fixture, selfUrl);
  return precedentFor(
    {
      coverage: "unavailable",
      customer_code: "",
      customer_name: null,
      spec_label: null,
      quantity: 0,
      rows: [],
    },
    selfUrl,
  );
}

/** Precedent for a catalogue price preview: keyed on the customer the CSR
 *  chose. No customer → the lookup cannot run (`unavailable`), which is the
 *  honest state while pricing against the central price group. */
export function mockPrecedentForCataloguePreview(
  customerCode: string | null | undefined,
  quantity: number,
): PricePrecedent {
  return catalogueFixture(customerCode, quantity);
}

function catalogueFixture(
  customerCode: string | null | undefined,
  quantity: number,
): PricePrecedentView {
  if (!customerCode) {
    return precedentFor({
      coverage: "unavailable",
      customer_code: "",
      customer_name: "",
      spec_label: null,
      quantity,
      rows: [],
    });
  }
  // The mock Ramsay catalogue (RAM1379) — the appointment-card cluster from
  // the validation run: a stable price ladder by quantity, reordered monthly.
  if (customerCode === "RAM1379") {
    return precedentFor({
      coverage: "recorded",
      customer_code: customerCode,
      customer_name: "Ramsay Healthcare",
      spec_label: "55×90 · 4/4 · 2pp · NC-1890-315-R",
      quantity,
      rows: [
        sold("2026-07-30", 1_000, 10_400, 0.36, "J-118010", "customer_ref"),
        sold("2026-07-29", 3_000, 17_200, 0.38, "J-117998", "customer_ref"),
        sold("2026-07-02", 1_000, 10_400, 0.36, "J-117230", "customer_ref"),
        sold("2026-06-22", 1_000, 10_400, 0.36, "J-116880", "customer_ref"),
        sold("2026-04-27", 1_000, 10_400, 0.36, "J-115260", "customer_ref"),
        sold("2025-12-04", 500, 8_700, 0.31, "J-111930", "customer_ref"),
        sold("2025-10-16", 500, 8_700, 0.31, "J-110870", "customer_ref"),
      ],
    });
  }
  // Business School UK on the standard business card (the costed fixture):
  // 250 last went at £52 — above today's £48 policy, so it is the suggestion;
  // 500 last went at £34.50 eighteen months ago, and today's cost has moved
  // enough that it no longer clears the digital floor, so policy is.
  // The mock customer list carries the short code (BSCH); the estimate
  // fixtures the full one (BSCH001). Both are Business School UK.
  if (customerCode === "BSCH" || customerCode === "BSCH001") {
    return precedentFor({
      coverage: "recorded",
      customer_code: customerCode,
      customer_name: "Business School UK",
      spec_label: "85×55 · 4/4 · 350gsm · BSC-BC",
      quantity,
      rows: [
        sold(
          "2026-02-11",
          250,
          5_200,
          0.59,
          "J-113920",
          "catalogue_exact",
          "catalogue",
        ),
        sold("2025-11-04", 250, 5_200, 0.59, "J-111340", "customer_ref"),
        sold("2025-06-19", 500, 3_450, 0.12, "J-108102", "customer_ref"),
        quoted("2026-04-14", 500, 5_800, "HDR-2026-0412", "est_093"),
        quoted("2026-08-30", 1_000, 9_200, "HDR-2026-0733", "est_092"),
      ],
    });
  }
  if (customerCode === "cust_003" || customerCode === "HOME01") {
    return precedentFor({
      coverage: "recorded",
      customer_code: customerCode,
      customer_name: "Home Group Limited",
      spec_label: "A6 · 5/0 · 2pp · H.9040.181",
      quantity,
      rows: HOME_GROUP_CALLING_CARD_ROWS,
    });
  }
  return precedentFor({
    coverage: "none",
    customer_code: customerCode,
    customer_name: customerCode,
    spec_label: null,
    quantity,
    rows: [],
  });
}

// ── Contracted rates (pricing_module's agreed price list, mocked) ─────────

interface MockContractRate {
  customer_code: string;
  quantity: number;
  price_pence: number;
  effective_from: string;
  set_by: string;
  note: string | null;
}

/** Business School UK's framework rate on 250 business cards, agreed early
 *  2025 — over a year old by the mock's clock, so it shows the stale flag. */
const CONTRACT_RATES: MockContractRate[] = [
  {
    customer_code: "BSCH",
    quantity: 250,
    price_pence: 5_000,
    effective_from: "2025-02-10",
    set_by: "Chris Smyth",
    note: "2025 framework — business cards, all sites",
  },
  {
    customer_code: "BSCH001",
    quantity: 250,
    price_pence: 5_000,
    effective_from: "2025-02-10",
    set_by: "Chris Smyth",
    note: "2025 framework — business cards, all sites",
  },
];

const MOCK_TODAY = new Date("2026-09-15");

function contractFor(
  customerCode: string,
  quantity: number,
): MockContractRate | null {
  return (
    CONTRACT_RATES.find(
      (r) =>
        r.customer_code === customerCode &&
        isNearQuantity(r.quantity, quantity),
    ) ?? null
  );
}

/** Prior jobs a CSR can link when the engine recognises nothing (search and
 *  link). Keyed loosely so any customer with no history gets a plausible list. */
export function mockPrecedentCandidates(
  customerCode: string | null | undefined,
  query: string,
): PrecedentCandidate[] {
  if (!customerCode) return [];
  const all: PrecedentCandidate[] = [
    {
      reference: "J-112480",
      kind: "job",
      date: "2026-01-20",
      description: "Business cards 85×55 · 4/4 · 350gsm — 3 names",
      quantity: 250,
      price_pence: 4_950,
      url: "/jobs/J-112480",
    },
    {
      reference: "J-109877",
      kind: "job",
      date: "2025-09-03",
      description: "Business cards — new starters, 2 versions",
      quantity: 500,
      price_pence: 6_900,
      url: "/jobs/J-109877",
    },
    {
      reference: "HDR-2026-0188",
      kind: "estimate",
      date: "2026-02-26",
      description: "Business cards 90×55 · 4/4 · 400gsm (not ordered)",
      quantity: 250,
      price_pence: 5_400,
      url: "/estimates/est_094",
    },
  ];
  const q = query.trim().toLowerCase();
  return q
    ? all.filter((c) =>
        `${c.reference} ${c.description}`.toLowerCase().includes(q),
      )
    : all;
}

// ── Price levels ──────────────────────────────────────────────────────────

/** Work-style review floors, as the pricing-targets page would hold them
 *  (calibration write-up recommendations). `other` and creative carry none. */
const WORK_STYLE_FLOOR_PCT: Record<string, number | null> = {
  litho: 0.22,
  digital: 0.2,
  wide_format: 0.25,
  factored: 0.18,
  mailing: 0.2,
  postage: 0.13,
  merch: 0.18,
  clothing: 0.18,
  creative: null,
  other: null,
};

const PRICE_GROUP_CONTRIBUTION_SOFT = 0.08;
const PRICE_GROUP_CONTRIBUTION_HARD = 0.0;
const REPEAT_THRESHOLDS: RepeatPriceThresholds = {
  up_approval: 10,
  down_approval: 8,
  up_hard: 25,
  down_hard: 20,
};

/**
 * The two levels for one quantity and which is suggested. Mirrors what the
 * backend will compute inside `resolve_price` once the hierarchy lands there:
 * the last ordered price is suggested only when it still clears BOTH the
 * work-style floor and the price group's contribution guardrail at today's
 * cost; with no cached cost the floor cannot be checked, so it is selectable
 * but not suggested.
 */
export function mockPriceLevels(args: {
  precedent: PricePrecedent | undefined;
  quantity: number;
  policyPence: number;
  costPence: number | null;
  workStyle: string;
}): PriceLevels {
  const { precedent, quantity, policyPence, costPence, workStyle } = args;
  const floor = WORK_STYLE_FLOOR_PCT[workStyle] ?? null;
  const cm = (pricePence: number) =>
    costPence == null || pricePence <= 0
      ? null
      : (pricePence - costPence) / pricePence;

  const policy: PriceLevel = {
    kind: "policy",
    price_pence: policyPence,
    unit_price_pence: quantity > 0 ? policyPence / quantity : 0,
    source: null,
    contribution_margin_pct: cm(policyPence),
    verdict: costPence == null ? "unchecked" : "ok",
    verdict_reason: null,
    suggestable: true,
    not_suggestable_reason: null,
    // The levers that built the number — what the CSR sees behind "policy".
    levers: [
      {
        label: `${workStyle} target`,
        value: floor == null ? "—" : `${Math.round((floor + 0.35) * 100)}% CM`,
      },
      { label: "Conversion", value: "Track market · neutral" },
      { label: "Capacity", value: "not active" },
      { label: "Speed", value: "Standard · 0%" },
      { label: "Commitment", value: "none" },
    ],
  };

  // Level 0: a contracted rate in place for this customer and spec. Suggested
  // whenever it exists; a stale one (older than a year) is flagged, not hidden.
  const contract = precedent
    ? contractFor(precedent.customer_code, quantity)
    : null;
  const contracted: PriceLevel | null = contract
    ? {
        kind: "contracted",
        price_pence: contract.price_pence,
        unit_price_pence: quantity > 0 ? contract.price_pence / quantity : 0,
        source: null,
        contribution_margin_pct: cm(contract.price_pence),
        verdict: cm(contract.price_pence) == null ? "unchecked" : "ok",
        verdict_reason: null,
        suggestable: true,
        not_suggestable_reason: null,
        contract: {
          effective_from: contract.effective_from,
          set_by: contract.set_by,
          is_stale:
            MOCK_TODAY.getTime() - new Date(contract.effective_from).getTime() >
            365 * 24 * 3600 * 1000,
          note: contract.note,
        },
      }
    : null;

  // Shown at calculation as a ceiling hint: the same spec quoted and never
  // ordered. Selectable, never suggested.
  const quotedRow =
    precedent?.coverage === "recorded"
      ? ((precedent.rows ?? [])
          .filter(
            (r) =>
              r.outcome === "quoted" && isNearQuantity(r.quantity, quantity),
          )
          .sort((a, b) => b.date.localeCompare(a.date))[0] ?? null)
      : null;
  const lastQuoted: PriceLevel | null = quotedRow
    ? {
        kind: "last_quoted",
        price_pence:
          quotedRow.quantity === quantity
            ? quotedRow.price_pence
            : Math.round(quotedRow.unit_price_pence * quantity),
        unit_price_pence: quotedRow.unit_price_pence,
        source: quotedRow,
        contribution_margin_pct: cm(
          quotedRow.quantity === quantity
            ? quotedRow.price_pence
            : Math.round(quotedRow.unit_price_pence * quantity),
        ),
        verdict: "unchecked",
        verdict_reason: null,
        suggestable: false,
        not_suggestable_reason:
          "Quoted but never ordered — a ceiling, not a precedent",
      }
    : null;

  const nearest =
    precedent?.coverage === "recorded"
      ? newestNear(precedent.rows ?? [], quantity, "ordered")
      : null;
  if (!nearest) {
    return {
      suggested: contracted ? "contracted" : "policy",
      levels: [contracted, lastQuoted, policy].filter(
        (l): l is PriceLevel => l != null,
      ),
      unrecognised:
        !contracted && !lastQuoted && precedent?.coverage !== "unavailable",
      work_style: workStyle,
      work_style_floor_pct: floor,
      drop_to_policy: null,
    };
  }
  const lastPence =
    nearest.quantity === quantity
      ? nearest.price_pence
      : Math.round(nearest.unit_price_pence * quantity);
  const lastCm = cm(lastPence);
  let verdict: LevelVerdict = "ok";
  let reason: string | null = null;
  let suggestable = true;
  let notWhy: string | null = null;
  if (lastCm == null) {
    verdict = "unchecked";
    suggestable = false;
    notWhy =
      "No manufacturing cost cached for this quantity, so the margin floor cannot be checked";
  } else if (lastCm < PRICE_GROUP_CONTRIBUTION_HARD) {
    verdict = "blocked";
    reason = `Would be ${(lastCm * 100).toFixed(1)}% CM at today's cost — below the 0% hard limit`;
    suggestable = false;
    notWhy = reason;
  } else if (floor != null && lastCm < floor) {
    verdict = "approval_required";
    reason = `Would be ${(lastCm * 100).toFixed(1)}% CM at today's cost — below the ${Math.round(floor * 100)}% ${workStyle} floor`;
    suggestable = false;
    notWhy = reason;
  } else if (lastCm < PRICE_GROUP_CONTRIBUTION_SOFT) {
    verdict = "approval_required";
    reason = `Would be ${(lastCm * 100).toFixed(1)}% CM at today's cost — below the 8% approval threshold`;
    suggestable = false;
    notWhy = reason;
  }
  const lastOrdered: PriceLevel = {
    kind: "last_ordered",
    price_pence: lastPence,
    unit_price_pence: quantity > 0 ? lastPence / quantity : 0,
    source: nearest,
    contribution_margin_pct: lastCm,
    verdict,
    verdict_reason: reason,
    suggestable,
    not_suggestable_reason: notWhy,
  };
  const movePct =
    lastPence > 0 ? ((policyPence - lastPence) / lastPence) * 100 : 0;
  return {
    suggested: contracted
      ? "contracted"
      : suggestable
        ? "last_ordered"
        : "policy",
    levels: [contracted, lastOrdered, lastQuoted, policy].filter(
      (l): l is PriceLevel => l != null,
    ),
    unrecognised: false,
    work_style: workStyle,
    work_style_floor_pct: floor,
    // The repeat-price guardrail governs a CSR's drop from a suggested last
    // ordered price. When that price is not suggestable the drop has no
    // basis, so no verdict is offered — the picker still shows the distance.
    drop_to_policy: suggestable
      ? {
          move_pct: movePct,
          verdict: repeatPriceVerdict(movePct, REPEAT_THRESHOLDS),
        }
      : null,
  };
}

// ── Repeat-price calibration ───────────────────────────────────────────────

/**
 * An empirical sample of unit-price moves (%) for same-customer repeats at
 * ±10% quantity over six months, shaped on the validation run's structural
 * key distribution: ~80% identical, p05 −14, p95 +16, thin tails to ±50.
 * The read-out computes verdict shares from this sample so the admin sees
 * the volume change as the thresholds are edited.
 */
const REPEAT_MOVE_SAMPLE: number[] = [
  ...Array<number>(158).fill(0),
  ...[
    -2, -1.5, -1, -0.8, -0.5, 0.4, 0.6, 1, 1.2, 1.7, 2, 2.4, 3, 3.1, 3.5, 4,
    4.4,
  ],
  ...[
    -13.8, -12, -10, -9.5, -9, -8.4, -7, -6.2, -5, 5.2, 5.9, 6.8, 7.5, 8.9, 9.4,
    10.4, 11.2, 12.1, 14, 16.1,
  ],
  ...[-50, -35, -27, -22, -20.5, 18, 21, 24, 29, 41],
];

export function mockRepeatPriceCalibration(
  priceGroupId: string,
  thresholds: RepeatPriceThresholds,
): RepeatPriceCalibration {
  const n = REPEAT_MOVE_SAMPLE.length;
  let ok = 0;
  let approval = 0;
  let blocked = 0;
  for (const move of REPEAT_MOVE_SAMPLE) {
    const v = repeatPriceVerdict(move, thresholds);
    if (v === "ok") ok++;
    else if (v === "approval_required") approval++;
    else blocked++;
  }
  const pct = (x: number) => Math.round((1000 * x) / n) / 10;
  return {
    type: "repeat_price_calibration",
    _self: `/views/price-groups/${priceGroupId}/repeat-price-calibration`,
    price_group_id: priceGroupId,
    price_group_name: "Standard trade",
    window_months: 6,
    quantity_tolerance_pct: 10,
    // Scaled to the validation population so the number reads as real volume.
    pairs: 9_361,
    pass_pct: pct(ok),
    approval_pct: pct(approval),
    block_pct: pct(blocked),
    identical_pct: pct(
      REPEAT_MOVE_SAMPLE.filter((m) => Math.abs(m) < 0.5).length,
    ),
    computed_at: REFRESHED_AT,
  };
}

// ── Catalogue price review ────────────────────────────────────────────────
//
// Shaped on what production will actually show: the cross-customer spread is
// keyed on the structural spec key, and today that resolves for 2 of 27
// dictionary items (the page count is unmappable — catalogue update summary).
// So most rows are `unkeyed`: a policy price with nothing to set beside it.

const review = (
  item_id: string,
  item_name: string,
  quantity: number,
  policy: number,
  spread: [number, number, number, number] | "unkeyed",
): CataloguePriceReviewRow => {
  const keyed = spread !== "unkeyed";
  return {
    type: "catalogue_price_review_row",
    _self: `/catalogue/items/${item_id}`,
    item_id,
    item_name,
    quantity,
    policy_pence: policy,
    spec_label: keyed ? item_name : null,
    precedent_coverage: keyed ? "recorded" : "unkeyed",
    orders: keyed ? spread[3] : 0,
    precedent: keyed
      ? { p25_pence: spread[0], median_pence: spread[1], p75_pence: spread[2] }
      : null,
    delta_vs_median_pct: keyed
      ? Math.round(((policy - spread[1]) / spread[1]) * 1000) / 10
      : null,
    item_url: `/settings/catalogues/cat-standard?item=${item_id}`,
  };
};

const REVIEW_ROWS: CataloguePriceReviewRow[] = [
  review(
    "itm-business-card",
    "Business card 55×85",
    250,
    5_820,
    [2_800, 4_500, 5_800, 184],
  ),
  review(
    "itm-business-card",
    "Business card 55×85",
    500,
    7_140,
    [3_700, 5_600, 7_200, 121],
  ),
  review(
    "itm-business-card",
    "Business card 55×85",
    1_000,
    9_600,
    [6_900, 8_100, 9_000, 4],
  ),
  review(
    "itm-letterhead-a4",
    "Letterhead A4 1/0",
    1_000,
    30_500,
    [29_800, 30_700, 31_000, 41],
  ),
  review("itm-calling-card-a6", "Calling card A6 5/0", 500, 8_150, "unkeyed"),
  review("itm-notepad-a5", "Notepad A5 50 leaves", 100, 21_400, "unkeyed"),
  review("itm-roller-banner", "Roller banner 2000×1000", 1, 15_900, "unkeyed"),
  review("itm-a5-8pp", "A5 8pp brochure", 500, 26_100, "unkeyed"),
  review(
    "itm-compliment-slip",
    "Compliment slip 99×210",
    500,
    9_200,
    "unkeyed",
  ),
  review("itm-ncr-set", "NCR set A5 3-part", 250, 24_600, "unkeyed"),
  review("itm-folder-a4", "Presentation folder A4", 250, 41_200, "unkeyed"),
];

export function mockCataloguePriceReview(opts?: {
  priceGroupId?: string;
  minOrders?: number;
}): CataloguePriceReview {
  const minOrders = opts?.minOrders ?? 5;
  const priceGroupId = opts?.priceGroupId ?? "pg-central";
  // Stands in for the server: a thin sample is withheld but counted, then
  // furthest-from-the-median first, rows with no delta last.
  const rows = REVIEW_ROWS.map((r) =>
    r.precedent_coverage === "recorded" && r.orders < minOrders
      ? {
          ...r,
          precedent_coverage: "under_min_orders" as const,
          precedent: null,
          delta_vs_median_pct: null,
        }
      : r,
  ).sort(
    (a, b) =>
      Number(a.delta_vs_median_pct == null) -
        Number(b.delta_vs_median_pct == null) ||
      Math.abs(b.delta_vs_median_pct ?? 0) -
        Math.abs(a.delta_vs_median_pct ?? 0),
  );
  return {
    type: "catalogue_price_review",
    _self: `/views/catalogue-price-review?price_group_id=${priceGroupId}`,
    price_group_id: priceGroupId,
    price_group_name: "Standard trade",
    version: 14,
    window_months: 6,
    min_orders: minOrders,
    refreshed_at: REFRESHED_AT,
    uncosted_rung_count: 3,
    rows,
  };
}
